import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";

// Branded Excel export, matching the PDF's look: an orange title band with
// the report name and generated-at stamp, a colored header row, bordered
// zebra-striped data rows, right-aligned numeric columns with the store's
// currency symbol on money columns specifically (not every number - a count
// or a quantity column stays a plain number), and auto-sized columns.
//
// The `xlsx` (SheetJS) package used everywhere else in this app for
// import/export (ItemsList.js, BulkItemUpload.js) is the free/community
// build, which cannot write cell styles at all in the browser - fills,
// fonts and borders are a paid-tier feature there. exceljs is the
// actively-maintained free alternative that actually supports them, so it's
// used here instead; the plain xlsx.utils path elsewhere is untouched.
const ACCENT = "FFEF7F1A"; // matches palette.accent in server/documents/_theme.js
const ACCENT_DARK = "FFC96A12";
const HEADER_TEXT = "FFFFFFFF";
const ZEBRA_FILL = "FFFAFBFC";
const BORDER_COLOR = "FFE3E8EF";

const thinBorder = { style: "thin", color: { argb: BORDER_COLOR } };

// Same idea as every report's `format: (r) => \`${currencySymbol}${formatMoney(...)}\`
// column definitions - a header naming an amount vs. a header naming a count
// or quantity. Deliberately conservative (word-boundary matches) so e.g.
// "Invoices" (a count) or "Qty Sold" never gets mistaken for money.
const MONEY_HEADER_PATTERN = /\b(amount|total|value|discount|tax|revenue|cost|profit|outstanding|sales|sub ?total|balance|price)\b/i;

// Normalises the same three shapes the server's own logoSrc()
// (server/documents/_pageChrome.js) already tolerates - a data: URI, a
// fetchable URL, or raw base64 with no prefix (Company.logoBase64) - into
// something workbook.addImage() can embed. Returns null (never throws) on
// anything unreadable, so a broken/missing logo just means no image instead
// of a failed export.
async function resolveLogoImage(logo) {
  const raw = String(logo || "").trim();
  if (!raw) return null;
  try {
    if (raw.startsWith("data:")) {
      const match = raw.match(/^data:image\/(png|jpe?g|gif);base64,/i);
      if (!match) return null;
      const ext = match[1].toLowerCase();
      return { base64: raw, extension: ext === "jpg" ? "jpeg" : ext };
    }
    if (/^https?:\/\//i.test(raw)) {
      const response = await fetch(raw);
      if (!response.ok) return null;
      const blob = await response.blob();
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      return { base64, extension: blob.type.includes("jpeg") ? "jpeg" : "png" };
    }
    // Raw base64 with no prefix - assume PNG, same default the server side uses.
    return { base64: `data:image/png;base64,${raw}`, extension: "png" };
  } catch (err) {
    return null;
  }
}

function isNumericColumn(rows, colIndex) {
  return rows.length > 0 && rows.every((row) => {
    const value = row[colIndex];
    return value === "" || value === null || value === undefined || typeof value === "number";
  });
}

// Excel's custom number-format syntax needs literal text quoted - build
// `"฿"#,##0.00` rather than embedding the symbol unescaped, which Excel
// would otherwise try to parse as format syntax.
function moneyNumFmt(currencySymbol) {
  const escaped = String(currencySymbol).replace(/"/g, '\\"');
  return `"${escaped}"#,##0.00`;
}

export async function exportRowsToExcel({ fileNamePrefix, sheetName = "Report", headers, rows, subtitle, currencySymbol, logo }) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "POS Reports";
  workbook.created = new Date();

  // Resolved before the sheet is created since it decides whether a
  // dedicated logo row is inserted above the title - and therefore which
  // row the frozen-pane split (ySplit) and every row number below need to
  // point at.
  const logoImage = await resolveLogoImage(logo);
  const titleRow = logoImage ? 2 : 1;
  const subtitleRow = titleRow + 1;
  const headerRowNumber = subtitleRow + 1;

  const sheet = workbook.addWorksheet(sheetName.slice(0, 31) || "Report", {
    views: [{ state: "frozen", ySplit: headerRowNumber }],
  });

  const colCount = headers.length;
  const numericCols = headers.map((_, i) => isNumericColumn(rows, i));
  const moneyCols = headers.map((header, i) => numericCols[i] && currencySymbol && MONEY_HEADER_PATTERN.test(header));

  // Logo band, spanning every column - same left-aligned placement as the
  // PDF's own masthead (server/documents/_pageChrome.js's reportHeader).
  if (logoImage) {
    sheet.mergeCells(1, 1, 1, colCount);
    sheet.getRow(1).height = 34;
    const imageId = workbook.addImage({ base64: logoImage.base64, extension: logoImage.extension });
    sheet.addImage(imageId, { tl: { col: 0, row: 0 }, ext: { width: 120, height: 40 }, editAs: "oneCell" });
  }

  // Title band, spanning every column - same idea as the PDF's masthead
  // (see server/documents/_pageChrome.js's reportHeader): report name on
  // its own row, generated-at stamp underneath.
  sheet.mergeCells(titleRow, 1, titleRow, colCount);
  const titleCell = sheet.getCell(titleRow, 1);
  titleCell.value = sheetName;
  titleCell.font = { bold: true, size: 15, color: { argb: ACCENT_DARK } };
  titleCell.alignment = { vertical: "middle" };
  sheet.getRow(titleRow).height = 26;

  sheet.mergeCells(subtitleRow, 1, subtitleRow, colCount);
  const subtitleCell = sheet.getCell(subtitleRow, 1);
  subtitleCell.value = subtitle || `Generated ${moment().format("MMM DD, YYYY [at] hh:mm A")}`;
  subtitleCell.font = { italic: true, size: 10, color: { argb: "FF7B8794" } };
  sheet.getRow(subtitleRow).height = 18;

  // Header row
  const headerRow = sheet.addRow(headers);
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, color: { argb: HEADER_TEXT }, size: 11 };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ACCENT } };
    cell.alignment = { vertical: "middle", horizontal: numericCols[colNumber - 1] ? "right" : "left" };
    cell.border = { top: thinBorder, bottom: thinBorder, left: thinBorder, right: thinBorder };
  });
  headerRow.height = 22;

  // Data rows
  rows.forEach((row, rowIndex) => {
    const dataRow = sheet.addRow(row);
    const isEven = rowIndex % 2 === 1;
    dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const colIndex = colNumber - 1;
      cell.border = { bottom: thinBorder };
      cell.alignment = { horizontal: numericCols[colIndex] ? "right" : "left" };
      if (isEven) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: ZEBRA_FILL } };
      if (moneyCols[colIndex] && typeof cell.value === "number") {
        // Money always shows 2 decimals regardless of whether this
        // particular value happens to be a whole number - ฿5 should read
        // "฿5.00", not "฿5".
        cell.numFmt = moneyNumFmt(currencySymbol);
      } else if (numericCols[colIndex] && typeof cell.value === "number" && !Number.isInteger(cell.value)) {
        cell.numFmt = "#,##0.00";
      } else if (numericCols[colIndex] && typeof cell.value === "number") {
        cell.numFmt = "#,##0";
      }
    });
  });

  // Auto-size columns from the widest cell in header/title/data, capped so
  // one long note field can't blow the sheet out to an unusable width.
  headers.forEach((header, i) => {
    const longest = rows.reduce((max, row) => {
      const text = row[i] == null ? "" : String(row[i]);
      return Math.max(max, text.length);
    }, String(header).length);
    sheet.getColumn(i + 1).width = Math.min(38, Math.max(10, longest + 2));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, `${fileNamePrefix}_${new Date().toISOString().slice(0, 10)}.xlsx`);
}
